import { useEffect, useState } from "react";
import { t } from "i18next";
import {
  Routes,
  Route,
  Outlet,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { useKeycloak } from "@react-keycloak/web";
import GitInfo from "react-git-info/macro";
import packageJSON from "../package.json";

import { useGetMapsQuery } from "@redux/services/mms";

import { useSetInitialProjectData } from "@hooks";

import { getProjectNames } from "@utils";

import ProtectedRoute from "./components/ProtectedRoute/ProtectedRoute";
import { FeaturesView, WorkflowsView, ShortLinksView } from "./screens";
import ListView from "./screens/FeaturesView/screens/ListView/ListView";
import MapView from "./screens/FeaturesView/screens/MapView/MapView";
import UnsavedChanges from "./screens/FeaturesView/Modal/UnsavedChanges";

import styles from "./App.module.css";
import NavigationRail from "@components/NavigationRail/NavigationRail";
import { useAppSelector } from "@redux/hooks";
import { Dialog } from "@livingmap/hippo";

const { commit, branch } = GitInfo();

const BaseComponent = () => {
  const { pathname } = useLocation();
  const { data } = useGetMapsQuery();
  const projectNames = getProjectNames(data);
  const navigate = useNavigate();

  useSetInitialProjectData(projectNames);

  // This useEffect is used to log the version, branch and commit hash of the app on load.
  useEffect(() => {
    // Set the document direction to "ltr" (left-to-right) by default. TODO: replace with config when available
    document.documentElement.setAttribute("dir", "ltr");

    console.log(
      `Version: ${packageJSON.version}\nBranch: ${branch}\nCommit hash: ${commit.hash}`,
    );
  }, []);

  return (
    <div className={styles.base}>
      <div className={styles.navigationRailContainer}>
        <NavigationRail
          dataQA="menu-sidebar"
          options={[
            {
              name: t("sidebar.menu_items.map"),
              icon: "map",
              onClick: () => navigate("/features"),
              active: pathname.includes("/features"),
            },
            {
              name: t("sidebar.menu_items.workflows"),
              icon: "play_circle",
              onClick: () => navigate("/workflows"),
              active: pathname.includes("/workflows"),
            },
            {
              name: t("sidebar.menu_items.links"),
              icon: "qr_code",
              onClick: () => navigate("/links"),
              active: pathname.includes("/links"),
            },
          ]}
          initialCollapsed={true}
          buttonTooltip="Collapse"
        />
      </div>
      <Outlet />
    </div>
  );
};

function App() {
  const { keycloak } = useKeycloak();
  const { hasUnsavedChanges } = useAppSelector((state) => state.application);
  const [isUnsavedChangesModalActive, setUnsavedChangesModalActive] =
    useState(false);

  const handleSignout = () => {
    if (hasUnsavedChanges) return setUnsavedChangesModalActive(true);

    keycloak.logout({ redirectUri: process.env.REACT_APP_BASE_URL });
  };

  const handleConfirmUnsavedChanges = () => {
    keycloak.logout({ redirectUri: process.env.REACT_APP_BASE_URL });
  };

  return (
    <div className={styles.container}>
      <Routes>
        <Route
          path="/"
          element={<Navigate replace to="/features/map-view" />}
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <BaseComponent />
            </ProtectedRoute>
          }
        >
          <Route
            path="/features"
            element={<Navigate replace to="/features/map-view" />}
          />
          <Route
            path="/features/*"
            element={<FeaturesView onSignOut={handleSignout} />}
          >
            <Route path="list-view" element={<ListView />} />
            <Route path="map-view" element={<MapView />} />
          </Route>
          <Route
            path="/workflows"
            element={<WorkflowsView onSignOut={handleSignout} />}
          />
          <Route
            path="/links"
            element={<ShortLinksView onSignOut={handleSignout} />}
          />
        </Route>
      </Routes>
      <ToastContainer
        theme="colored"
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <Dialog
        dataQA="unsaved-changes-modal"
        isOpen={isUnsavedChangesModalActive}
        onClose={() => setUnsavedChangesModalActive(false)}
      >
        <UnsavedChanges
          onCancel={() => setUnsavedChangesModalActive(false)}
          onConfirm={handleConfirmUnsavedChanges}
        />
      </Dialog>
    </div>
  );
}

export default App;
